import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import {
  Stage,
  ChannelType,
  StageTimerBehaviorTypes,
} from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { updateChannelEvents } from '@/actions/channelEvents'
import { ChannelVideoDebugInfo } from '@/components/ChannelVideoDebugInfo/ChannelVideoDebugInfo'
import { Scrollbar as StageProgress } from '@/components/Scrollbar/Scrollbar'
import { VideoPlaybackStatus } from '@/constants/common'
import { usePageOptionsUpdate } from '@/hooks/usePageOptionsUpdate'
import useStore from '@/store/useStore'

import { StageBackground } from './HomeTabPageStageBgContainer'
import { StageTile } from './HomeTabPageStageTile'
import { useStageAutoAction } from '../hooks/useStageAutoAction'
import { useStageAutoslide } from '../hooks/useStageAutoslide'
import {
  StageContainer,
  StageTilesGroup,
  StageFooter,
  StageForeground,
} from '../styles'
import { getStageSlideMeta } from '../utils/utils'

type Props = {
  stageData?: Stage
  isVisible: boolean
  id: string
  currentTabIndex: number
  defaultSlideIndex: number
  isPopup?: boolean
  focusIdPrefix?: string
}

export const HomePageStage = ({
  stageData,
  isVisible = true,
  id,
  currentTabIndex,
  defaultSlideIndex,
  isPopup,
  focusIdPrefix = '',
}: Props) => {
  const stageContainerFocusId = `${focusIdPrefix}_StageContainer`
  const { isFullscreen } = useStore((state) => state.player)
  const broadcasts = useStore((state) => state.common.broadcasts)
  const isAppHidden = useStore((state) => state.common.isAppHidden)
  const setActiveBroadcastChannel = useStore(
    (state) => state.common.setActiveBroadcastChannel,
  )
  const setBroadcastChannelList = useStore(
    (state) => state.common.setBroadcastChannelList,
  )

  const { setSlideIndex } = usePageOptionsUpdate(isPopup)

  const setFocus = useSetFocus()
  const { isRegisteredUser } = useAuth()
  const [defaultFocusChild, setDefaultFocusChild] = useState(0)
  const [videoPlaybackStatus, setVideoPlaybackStatus] =
    useState<Nullable<VideoPlaybackStatus>>(null)
  const isVideoError = videoPlaybackStatus === VideoPlaybackStatus.ERROR

  const focusNode = useFocusNodeById(stageContainerFocusId)

  const setActiveSlide = useCallback(
    (index: number) => {
      setDefaultFocusChild(index)
      const activeSlide = stageData?.slides[index]
      const channel = activeSlide?.channel
      setSlideIndex(index)
      if (activeSlide?.type === 'Channel' && channel) {
        setActiveBroadcastChannel(id, channel)
        setVideoPlaybackStatus(null)
      }
    },
    [stageData],
  )

  const slidesLength = useMemo(() => stageData?.slides.length, [stageData])

  const activeChannel = broadcasts?.[id]?.activeChannel

  const channelEvent = useStore(
    (state) => state.common.channelEvents[activeChannel?.id || ''],
  )
  const currentEvent = channelEvent?.isFutureChannelEvent ? null : channelEvent

  const { isAccessible, isRequiresRegistrationOnly } = useMemo(() => {
    return getContentAccessFlags(currentEvent, activeChannel, isRegisteredUser)
  }, [currentEvent, activeChannel, isRegisteredUser])

  const isStageProgressVisible =
    stageData?.slides && stageData.slides.length > 1

  useEffect(() => {
    const channels = (stageData?.slides
      .map((slide) => slide.type === 'Channel' && slide.channel)
      .filter(Boolean) || []) as ChannelType[]
    if (!channels) {
      return
    }

    updateChannelEvents(isAppHidden, channels)
    setBroadcastChannelList(id, channels)
    return () => {
      setBroadcastChannelList(id, [])
      setActiveBroadcastChannel(id, null)
    }
  }, [id, stageData])

  useEffect(() => {
    const isCorrectSlide =
      !activeChannel ||
      stageData?.slides[defaultFocusChild]?.channel?.id === activeChannel.id
    if (isCorrectSlide) {
      return
    }
    const nextDefaultFocusChild =
      stageData?.slides.findIndex(
        (slide) => slide.channel?.id === activeChannel?.id,
      ) ?? -1
    if (activeChannel && nextDefaultFocusChild !== -1) {
      setDefaultFocusChild(nextDefaultFocusChild)
      if (!isAccessible) {
        focusNode?.isFocused &&
          setTimeout(
            setFocus,
            0,
            `${focusIdPrefix}_HomePageStageTile_${nextDefaultFocusChild}`,
          )
      }
    }
  }, [activeChannel?.id])

  /**
   * Switch to the first slide on tab change
   */
  useLayoutEffect(() => {
    setActiveSlide(defaultSlideIndex)
  }, [currentTabIndex])

  const onMove = (e: MoveEvent) => {
    const currentChildIndex =
      e.direction === 'forward'
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setActiveSlide(currentChildIndex)
  }

  const onRight = (e: LRUDEvent) => {
    if (Number(e.node.defaultFocusChild) === Number(slidesLength) - 1) {
      e.preventDefault()
      setActiveSlide(0)
      setFocus(`${focusIdPrefix}_HomePageStageTile_${0}`)
    }
  }

  const getStageVisibilityStatus = (
    index: number,
  ): 'isVisible' | 'isBefore' | 'isAfter' => {
    if (index === defaultFocusChild) {
      return 'isVisible'
    }
    if (index < defaultFocusChild) {
      return 'isBefore'
    }
    return 'isAfter'
  }

  const {
    activeStageSlide,
    backgroundMediaData,
    backgroundMediaDataId,
    backgroundMediaFormat,
    isBlockedStage,
    isButtonHidden,
    isShade,
    onEnded,
  } = useMemo(() => {
    const stageSlide = stageData?.slides[defaultFocusChild]
    return getStageSlideMeta(stageSlide, null, isFullscreen, isRegisteredUser)
  }, [stageData, defaultFocusChild, activeChannel, channelEvent, isFullscreen])

  /**
   * @description
   * Stage autofullscreen when user is inactive
   * disable autofullscreen if activeStageSlide?.timer === 0
   * SWD-184
   */
  useStageAutoAction({
    activeStageSlide,
    isBlockedStage,
    autoFullscreenTimout: activeStageSlide?.timer ?? stageData?.timer,
    isEnabled: Boolean(
      activeStageSlide?.timerBehaviour ===
        StageTimerBehaviorTypes.BUTTON_ACTION &&
        isVisible &&
        focusNode?.isFocused &&
        !isButtonHidden,
    ),
  })

  /**
   * @description
   * Stage autoslide when user is inactive
   * disable autoslide if activeStageSlide?.timer === 0
   * SWD-184
   */
  useStageAutoslide({
    isVideoError,
    defaultFocusChild,
    setDefaultFocusChild: setActiveSlide,
    slidesLength,
    stageDataTimer: activeStageSlide?.timer ?? stageData?.timer,
    focusIdPrefix,
    isEnabled: Boolean(
      activeStageSlide?.timerBehaviour === StageTimerBehaviorTypes.NEXT_SLIDE &&
        isVisible &&
        focusNode?.isFocused,
    ),
  })

  if (!stageData || !stageData.slides.length) {
    return null
  }

  const onProgressClick = (index: number) => {
    setActiveSlide(index)
    setFocus(`${focusIdPrefix}_HomePageStageTile_${index}`)
  }

  return (
    <StageContainer
      focusId={stageContainerFocusId}
      $isFullscreen={isFullscreen}
      $isVisible={isVisible}
      data-component="StageContainer"
    >
      {isDevMode && activeStageSlide?.type === 'Channel' && (
        <ChannelVideoDebugInfo
          eventChannel={activeChannel}
          event={channelEvent}
        />
      )}
      <StageBackground
        data-component="StageBackground"
        src={backgroundMediaData}
        id={backgroundMediaDataId}
        slideTitle={activeStageSlide?.title}
        srcType={backgroundMediaFormat}
        isFaded={!isFullscreen && isShade}
        isVisible={isVisible}
        broadcastId={id}
        onEnded={onEnded}
        setVideoPlaybackStatus={setVideoPlaybackStatus}
        focusIdPrefix={focusIdPrefix}
        isPopup={isPopup}
      />
      <StageForeground>
        <StageTilesGroup
          data-component="StageTilesGroup"
          defaultFocusChild={defaultFocusChild}
          orientation="horizontal"
          onMove={onMove}
          onRight={onRight}
        >
          {stageData.slides.map((stageSlide, index) => (
            <StageTile
              key={`${focusIdPrefix}_HomePageStageTile_${index}`}
              stageSlide={stageSlide}
              index={index}
              currentTabIndex={currentTabIndex}
              isRegisteredUser={isRegisteredUser}
              withAnimation
              visibilityStatus={getStageVisibilityStatus(index)}
              focusIdPrefix={focusIdPrefix}
              isPopup={isPopup}
              isRequiresRegistrationOnly={isRequiresRegistrationOnly}
            />
          ))}
        </StageTilesGroup>
        <StageFooter>
          {isStageProgressVisible && (
            <StageProgress
              length={stageData.slides.length}
              activeIndex={defaultFocusChild}
              onProgressClick={onProgressClick}
            />
          )}
        </StageFooter>
      </StageForeground>
    </StageContainer>
  )
}
